// @ts-nocheck
/**
 * ACL Settings list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + DataTable + DropdownMenu row actions + ConfirmDialog + shadcn dialogs).
 * Business logic preserved: default_for_role gate, is_allocated delete gate,
 * multi-select + bulk delete, view/users modals with search.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Check,
  Copy,
  Eye,
  Lock,
  LockOpen,
  Network,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_ACL_SETTING_LIST,
  API_ACL_SETTING_DELETE,
  API_ACL_SETTING_LOCK,
} from '@/service/url';
import {
  FreezePasswordManager,
  FreezePasswordPrompt,
  LockBadge,
  isAclLocked,
  isPermanentlyLockedAcl,
} from './aclLockUi';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { getAclModuleOptionLabels } from '@/utilities/aclOptionSummary';

const formatDate = (value: any) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';

const PermissionCell = ({ value }: { value: any }) =>
  value ? (
    <span
      className="mx-auto inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-500 text-white ring-1 ring-inset ring-emerald-600"
      title="Allowed"
    >
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
  ) : (
    <span
      className="mx-auto inline-flex h-6 w-6 items-center justify-center rounded-md bg-muted/50 text-muted-foreground/50 ring-1 ring-inset ring-border/70"
      title="Not allowed"
    >
      <X className="h-3 w-3" strokeWidth={2.5} />
    </span>
  );

const ACLSettingList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = usePersistedState<'mine' | 'nested' | 'default'>('acl-settings/ACLSettingList:activeTab', 'mine');
  const [loading, setLoading] = useState(false);
  const [selectedAcl, setSelectedAcl] = useState<any[]>([]);

  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [pendingBulk, setPendingBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);

  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  const [freezePrompt, setFreezePrompt] = useState<any>(null);
  const [showFreezeManager, setShowFreezeManager] = useState(false);

  const [selectedAclForView, setSelectedAclForView] = useState<any>(null);
  const [moduleSearch, setModuleSearch] = useState('');
  const [usersModalAcl, setUsersModalAcl] = useState<any>(null);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(
        parseModuleOperations(user.acl_settings.module_operations),
      );
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchList();
  }, []);

  const fetchList = () => {
    getApiCall(`${API_ACL_SETTING_LIST}?include_nested=true`)
      .then((resp: any) => {
        const currentUserRole = getUser()?.role;
        const list = (resp || [])
          .filter(
            (val: any) =>
              !(
                currentUserRole === USER_ROLES.CALL_CENTRE_ADMIN &&
                val.default_for_role === USER_ROLES.CALL_CENTRE_ADMIN
              ),
          )
          .map((val: any, idx: number) => ({
            ...val,
            key: idx + 1,
          }));
        setOriginalDataList(list);
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          console.log(err);
        }
      })
      .finally(() => setLoading(false));
  };

  const isNestedTab = activeTab === 'nested';
  const isDefaultTab = activeTab === 'default';
  const viewOnly = isNestedTab || (isDefaultTab && !isSuperAdmin);

  const [myAclList, nestedAclList, defaultAclList] = useMemo(() => {
    const mine: any[] = [];
    const nested: any[] = [];
    const defaults: any[] = [];
    originalDataList.forEach((row: any) =>
      (row?.default_for_role ? defaults : row?.is_nested ? nested : mine).push(row),
    );
    return [mine, nested, defaults];
  }, [originalDataList]);

  const aclSettingList = useMemo(() => {
    const source = isNestedTab ? nestedAclList : isDefaultTab ? defaultAclList : myAclList;
    const q = searchValue.trim().toLowerCase();
    return q
      ? source.filter((data: any) => (data.acl_name || '').toLowerCase().includes(q))
      : source;
  }, [isNestedTab, isDefaultTab, myAclList, nestedAclList, defaultAclList, searchValue]);

  const handleCheckboxToggle = (aclId: any) => {
    setSelectedAcl((prev) =>
      prev.includes(aclId) ? prev.filter((id) => id !== aclId) : [...prev, aclId],
    );
  };

  const aclNameById = useMemo(() => {
    const map: Record<string, string> = {};
    originalDataList.forEach((acl: any) => {
      map[String(acl.id)] = acl.acl_name;
    });
    return map;
  }, [originalDataList]);

  const cloneSourceLabel = (row: any) =>
    row?.is_cloned && row?.cloned_from_acl_id
      ? aclNameById[String(row.cloned_from_acl_id)] || `ACL #${row.cloned_from_acl_id}`
      : '';

  const selectableAclIds = useMemo(
    () =>
      viewOnly
        ? []
        : aclSettingList
            .filter((acl: any) => isSuperAdmin || !acl.default_for_role)
            .map((acl: any) => acl.id),
    [aclSettingList, isSuperAdmin, viewOnly],
  );

  const handleSelectAll = () => {
    if (selectedAcl.length === selectableAclIds.length) setSelectedAcl([]);
    else setSelectedAcl(selectableAclIds);
  };

  const confirmDelete = (freezePassword?: string) => {
    const ids = pendingBulk ? selectedAcl : pendingDelete ? [pendingDelete.id] : [];
    if (!ids.length) return;

    const lockedInBatch = aclSettingList.filter(
      (acl: any) => ids.includes(acl.id) && isAclLocked(acl),
    );
    if (lockedInBatch.length && !freezePassword && isSuperAdmin) {
      setFreezePrompt({
        aclName: lockedInBatch.map((a: any) => a.acl_name).join(', '),
        actionLabel: 'Delete',
        onConfirm: (password: string) => confirmDelete(password),
      });
      return;
    }

    setDeleting(true);
    return postApiCall(API_ACL_SETTING_DELETE, {
      aclIds: ids,
      ...(freezePassword ? { freeze_password: freezePassword } : {}),
    })
      .then(() => {
        setFreezePrompt(null);
        toast.success('ACL Setting deleted successfully');
        if (pendingBulk) setSelectedAcl([]);
        setPendingDelete(null);
        setPendingBulk(false);
        setLoading(true);
        fetchList();
      })
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
        } else {
          toast.error(friendlyApiError(err));
        }
      })
      .finally(() => setDeleting(false));
  };

  const toggleLock = (acl: any, shouldLock: boolean, freezePassword?: string) => {
    if (!shouldLock && !freezePassword) {
      setFreezePrompt({
        aclName: acl.acl_name,
        actionLabel: 'Unlock',
        onConfirm: (password: string) => toggleLock(acl, false, password),
      });
      return;
    }

    return postApiCall(API_ACL_SETTING_LOCK.replace(':id', acl.id), {
      is_locked: shouldLock,
      ...(freezePassword ? { freeze_password: freezePassword } : {}),
    })
      .then(() => {
        toast.success(shouldLock ? 'ACL frozen.' : 'ACL unfrozen.');
        setFreezePrompt(null);
        setLoading(true);
        fetchList();
      })
      .catch((err: any) => toast.error(friendlyApiError(err)));
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: isNestedTab ? 'acl-settings-nested' : 'acl-settings',
        data: aclSettingList,
        columns: [
          { header: 'ACL Name', accessor: 'acl_name' },
          ...(isNestedTab
            ? [
                {
                  header: 'Created By',
                  accessor: (row: any) => row?.creator?.username || '—',
                },
              ]
            : []),
          {
            header: 'ACL Allocated',
            accessor: (row: any) => (row.is_allocated ? 'Allocated' : 'Not Allocated'),
          },
          ...(isSuperAdmin
            ? [{ header: 'Frozen', accessor: (row: any) => (isAclLocked(row) ? 'Yes' : 'No') }]
            : []),
          ...(isSuperAdmin
            ? [
                {
                  header: 'Assigned Users',
                  accessor: (row: any) =>
                    row.assigned_user_count ?? (row.assigned_users?.length || 0),
                },
              ]
            : []),
          { header: 'Cloned From', accessor: (row: any) => cloneSourceLabel(row) || '—' },
          {
            header: 'Cloned At',
            accessor: (row: any) => (row.is_cloned ? formatDate(row.cloned_at) : '—'),
          },
          { header: 'Created Date', accessor: (row: any) => formatDate(row.created_at) },
          { header: 'Updated Date', accessor: (row: any) => formatDate(row.updated_at) },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'ACL Setting List',
    'read',
  );
  const aclOpsLoaded = Object.keys(moduleOperationsForReadPermissionCheck).length > 0;
  const canAdd =
    isSuperAdmin ||
    !aclOpsLoaded ||
    hasPermission(moduleOperationsForReadPermissionCheck, 'ACL Setting List', 'add');
  const canEdit =
    isSuperAdmin ||
    !aclOpsLoaded ||
    hasPermission(moduleOperationsForReadPermissionCheck, 'ACL Setting List', 'edit');
  const canDelete =
    isSuperAdmin ||
    !aclOpsLoaded ||
    hasPermission(moduleOperationsForReadPermissionCheck, 'ACL Setting List', 'delete');

  const selectedAclOperations = parseModuleOperations(selectedAclForView?.module_operations);
  const operationRows = Object.keys(selectedAclOperations).sort((a, b) => a.localeCompare(b));
  const modulesWithPermission = operationRows.filter((moduleName) => {
    const ops = selectedAclOperations[moduleName] || {};
    return ops.read || ops.add || ops.edit || ops.delete || ops.view_permission;
  });
  const visibleModules = moduleSearch
    ? modulesWithPermission.filter((m) =>
        m.toLowerCase().includes(moduleSearch.trim().toLowerCase()),
      )
    : modulesWithPermission;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const hideMeta = isDefaultTab && !isSuperAdmin;
    const cols: ColumnDef<any>[] = [
      ...(viewOnly
        ? []
        : [
            {
              id: '_select',
              size: 40,
              enableSorting: false,
              enableHiding: false,
              header: () => (
                <Checkbox
                  checked={
                    selectedAcl.length === selectableAclIds.length &&
                    selectableAclIds.length > 0
                  }
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  checked={selectedAcl.includes(row.original.id)}
                  onCheckedChange={() => handleCheckboxToggle(row.original.id)}
                  disabled={!isSuperAdmin && Boolean(row.original.default_for_role)}
                  aria-label="Select row"
                />
              ),
            },
          ]),
      {
        accessorKey: 'acl_name',
        header: 'ACL Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            {!viewOnly &&
            canEdit &&
            (isSuperAdmin || !row.original.default_for_role) &&
            (isSuperAdmin || loggedInUserACL[MODULE_ACL.ACL_SETTING_LIST]?.edit) ? (
              <Link
                to={`/${ROUTES.ACL_SETTING.EDIT.replace(':id', row.original.id)}`}
                className="font-medium text-foreground hover:underline"
              >
                {row.original.acl_name}
              </Link>
            ) : (
              <span className="font-medium text-foreground">{row.original.acl_name}</span>
            )}
            {isSuperAdmin ? <LockBadge acl={row.original} /> : null}
          </span>
        ),
      },
      ...(isNestedTab
        ? [
            {
              id: 'created_by_user',
              header: 'Created By',
              cell: ({ row }) => (
                <span className="text-sm text-foreground">
                  {row.original?.creator?.username || '—'}
                </span>
              ),
            },
          ]
        : []),
      ...(hideMeta
        ? []
        : [
            {
              accessorKey: 'is_allocated',
              header: 'Status',
              size: 140,
              cell: ({ row }) => (
                <span
                  className={
                    row.original.is_allocated
                      ? 'inline-flex rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400'
                      : 'inline-flex rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
                  }
                >
                  {row.original.is_allocated ? 'Allocated' : 'Not Allocated'}
                </span>
              ),
            },
          ]),
      ...(isSuperAdmin
        ? [
            {
              id: 'assigned_users',
              header: 'Assigned Users',
              cell: ({ row }) => {
                const rowData = row.original;
                const count =
                  rowData.assigned_user_count ?? (rowData.assigned_users?.length || 0);
                if (!count) return <span className="text-xs text-muted-foreground">0</span>;
                return (
                  <button
                    type="button"
                    className="text-sm font-medium text-primary hover:underline"
                    onClick={() => setUsersModalAcl(rowData)}
                  >
                    {count} {count === 1 ? 'user' : 'users'}
                  </button>
                );
              },
            },
          ]
        : []),
      ...(isDefaultTab
        ? []
        : [
            {
              id: 'cloned_from',
              header: 'Cloned From',
              cell: ({ row }) => {
                const label = cloneSourceLabel(row.original);
                if (!label) return <span className="text-xs text-muted-foreground">—</span>;
                return (
                  <span className="flex flex-col">
                    <span className="flex items-center gap-1 text-sm font-medium text-foreground">
                      <Copy className="h-3 w-3 text-muted-foreground" />
                      {label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(row.original.cloned_at)}
                    </span>
                  </span>
                );
              },
            },
          ]),
      ...(hideMeta
        ? []
        : [
            {
              accessorKey: 'created_at',
              header: 'Created',
              cell: ({ row }) => (
                <span className="text-xs text-muted-foreground">
                  {formatDate(row.original.created_at)}
                </span>
              ),
            },
            {
              accessorKey: 'updated_at',
              header: 'Updated',
              cell: ({ row }) => (
                <span className="text-xs text-muted-foreground">
                  {formatDate(row.original.updated_at)}
                </span>
              ),
            },
          ]),
      {
        id: 'actions',
        header: 'Actions',
        size: 130,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const rowData = row.original;
          const isDefaultAcl = Boolean(rowData.default_for_role);
          const canManageDefault = isSuperAdmin || !isDefaultAcl;
          const showEdit =
            !viewOnly &&
            canManageDefault &&
            canEdit &&
            (isSuperAdmin || loggedInUserACL[MODULE_ACL.ACL_SETTING_LIST]?.edit);
          const showClone =
            !viewOnly &&
            !isDefaultAcl &&
            canAdd &&
            (isSuperAdmin || loggedInUserACL[MODULE_ACL.ACL_SETTING_LIST]?.add) &&
            String(rowData.created_by) === String(getUser()?.id);
          const showDelete =
            !viewOnly &&
            canManageDefault &&
            canDelete &&
            (isSuperAdmin || loggedInUserACL?.[MODULE_ACL.ACL_SETTING_LIST]?.delete) &&
            selectedAcl.length === 0 &&
            !rowData.is_allocated;

          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedAclForView(rowData);
                  setModuleSearch('');
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                title="View permissions"
                aria-label="View permissions"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
              {showEdit && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/${ROUTES.ACL_SETTING.EDIT.replace(':id', rowData.id)}`);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                  title="Edit"
                  aria-label="Edit"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {showClone && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/${ROUTES.ACL_SETTING.CREATE}?clone=${rowData.id}`);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15"
                  title="Copy to a new ACL"
                  aria-label="Copy to a new ACL"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
              {!viewOnly && isSuperAdmin && !isPermanentlyLockedAcl(rowData) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLock(rowData, !isAclLocked(rowData));
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-amber-100 text-amber-700 ring-1 ring-inset ring-amber-200 transition-colors hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
                  title={isAclLocked(rowData) ? 'Unfreeze ACL' : 'Freeze ACL'}
                  aria-label={isAclLocked(rowData) ? 'Unfreeze ACL' : 'Freeze ACL'}
                >
                  {isAclLocked(rowData) ? (
                    <LockOpen className="h-3.5 w-3.5" />
                  ) : (
                    <Lock className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              {showDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    checkAuth(setToastProps, setShowToast);
                    setPendingDelete(rowData);
                  }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                  title="Delete"
                  aria-label="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        },
      },
    ];
    return cols;
  }, [
    selectedAcl,
    selectableAclIds,
    isSuperAdmin,
    isNestedTab,
    isDefaultTab,
    viewOnly,
    canEdit,
    canDelete,
    loggedInUserACL,
    aclNameById,
    navigate,
  ]);

  if (aclOpsLoaded && !canRead) {
    return (
      <FillPage>
        <PageHeader title="ACL Settings" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="ACL Settings"
        description="Named permission sets that gate module operations for each user role."
        actions={
          <>
            {!isNestedTab &&
              canDelete &&
              (isSuperAdmin || loggedInUserACL?.[MODULE_ACL.ACL_SETTING_LIST]?.delete) &&
              selectedAcl.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={() => setPendingBulk(true)}
                  disabled={selectedAcl.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected ({selectedAcl.length})
                </Button>
              )}
            {!isNestedTab && isSuperAdmin && (
              <Button variant="outline" onClick={() => setShowFreezeManager(true)}>
                <Lock className="h-4 w-4" />
                Freeze Password
              </Button>
            )}
            {aclSettingList.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                Export
              </Button>
            )}
            {!isNestedTab &&
              canAdd &&
              (isSuperAdmin || loggedInUserACL[MODULE_ACL.ACL_SETTING_LIST]?.add) && (
              <Button asChild>
                <Link to={`/${ROUTES.ACL_SETTING.CREATE}`}>
                  <Plus className="h-4 w-4" />
                  Create ACL Setting
                </Link>
              </Button>
            )}
          </>
        }
      />

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as 'mine' | 'nested' | 'default');
          setSelectedAcl([]);
        }}
        className="mb-4"
      >
        <TabsList className="h-auto max-w-full shrink-0 justify-start gap-1 overflow-x-auto rounded-full border border-border/60 bg-muted/40 p-1 [scrollbar-width:none] shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]">
          <TabsTrigger
            value="mine"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <ShieldCheck className="h-3.5 w-3.5" />
            Created By Me ({myAclList.length})
          </TabsTrigger>
          <TabsTrigger
            value="nested"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <Network className="h-3.5 w-3.5" />
            Created By Child Users ({nestedAclList.length})
          </TabsTrigger>
          <TabsTrigger
            value="default"
            className="gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]"
          >
            <Lock className="h-3.5 w-3.5" />
            Default ACLs ({defaultAclList.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        key={activeTab}
        tableId={
          isNestedTab
            ? 'acl-setting.nested:v1'
            : isDefaultTab
              ? 'acl-setting.default:v1'
              : 'acl-setting.list:v1'
        }
        columns={columns}
        data={aclSettingList}
        isLoading={loading}
        searchPlaceholder="Search by name…"
        emptyTitle={isNestedTab ? 'No nested ACL settings' : 'No ACL settings'}
        emptyDescription={
          isNestedTab
            ? 'None of your nested users have created an ACL setting yet.'
            : 'No ACL settings match the current filters.'
        }
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="acl-settings.csv"
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null || pendingBulk}
        onOpenChange={(o) => {
          if (!o) {
            setPendingDelete(null);
            setPendingBulk(false);
          }
        }}
        title={pendingBulk ? 'Delete selected ACL settings?' : 'Delete this ACL setting?'}
        description={
          pendingBulk ? (
            <>
              <span className="font-medium text-foreground">{selectedAcl.length}</span> ACL
              setting(s) will be permanently deleted. This action cannot be undone.
            </>
          ) : (
            <>
              ACL setting{' '}
              <span className="font-medium text-foreground">{pendingDelete?.acl_name}</span>{' '}
              will be permanently deleted.
            </>
          )
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={() => confirmDelete()}
      />

      <FreezePasswordPrompt
        open={freezePrompt !== null}
        onOpenChange={(o: boolean) => !o && setFreezePrompt(null)}
        aclName={freezePrompt?.aclName}
        actionLabel={freezePrompt?.actionLabel}
        onConfirm={(password: string) => freezePrompt?.onConfirm(password)}
      />

      <FreezePasswordManager
        open={showFreezeManager}
        onOpenChange={setShowFreezeManager}
      />

      {/* View permissions modal */}
      <Dialog
        open={selectedAclForView !== null}
        onOpenChange={(o) => {
          if (!o) {
            setSelectedAclForView(null);
            setModuleSearch('');
          }
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <ShieldCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">
                  ACL Permission View
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  Read-only summary of module operations granted by this ACL.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  ACL Name
                </span>
                <div className="text-sm font-semibold text-foreground">
                  {selectedAclForView?.acl_name || '—'}
                </div>
              </div>
              {selectedAclForView?.default_for_role && (
                <div>
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Default For Role
                  </span>
                  <div>
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25">
                      {selectedAclForView.default_for_role}
                    </span>
                  </div>
                </div>
              )}
              <div className="ml-auto">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total Modules
                </span>
                <div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25 tabular-nums">
                    {moduleSearch
                      ? `${visibleModules.length} of ${modulesWithPermission.length}`
                      : modulesWithPermission.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search module…"
                value={moduleSearch}
                onChange={(e) => setModuleSearch(e.target.value)}
                className="input-embossed h-10 w-full rounded-md pl-8 pr-3 text-sm"
              />
            </div>
            <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-emerald-500 text-white ring-1 ring-inset ring-emerald-600">
                  <Check className="h-2.5 w-2.5" strokeWidth={3} />
                </span>
                Allowed
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-muted/50 text-muted-foreground/60 ring-1 ring-inset ring-border/70">
                  <X className="h-2.5 w-2.5" strokeWidth={2.5} />
                </span>
                Not allowed
              </span>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Module
                  </th>
                  <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Read
                  </th>
                  <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Add
                  </th>
                  <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Edit
                  </th>
                  <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Delete
                  </th>
                  <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    View
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Additional Options
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleModules.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                      {moduleSearch
                        ? 'No modules match your search.'
                        : 'No module operations found.'}
                    </td>
                  </tr>
                ) : (
                  visibleModules.map((moduleName, idx) => {
                    const ops = selectedAclOperations[moduleName] || {};
                    const optionLabels = getAclModuleOptionLabels(moduleName, ops);
                    return (
                      <tr
                        key={moduleName}
                        className={
                          'border-t border-border/40 transition-colors hover:bg-muted/30 ' +
                          (idx % 2 === 1 ? 'bg-muted/15' : '')
                        }
                      >
                        <td className="px-4 py-2.5 font-medium text-foreground">
                          {moduleName}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <PermissionCell value={ops.read} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <PermissionCell value={ops.add} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <PermissionCell value={ops.edit} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <PermissionCell value={ops.delete} />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <PermissionCell value={ops.view_permission} />
                        </td>
                        <td className="px-4 py-2.5">
                          {optionLabels.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {optionLabels.map((label) => (
                                <span
                                  key={label}
                                  className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary ring-1 ring-inset ring-primary/25"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAclForView(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assigned users modal */}
      <Dialog
        open={usersModalAcl !== null}
        onOpenChange={(o) => !o && setUsersModalAcl(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Users className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Assigned Users</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {usersModalAcl?.acl_name
                    ? `Users currently granted the "${usersModalAcl.acl_name}" ACL.`
                    : 'Users currently granted this ACL.'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div>
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  ACL Name
                </span>
                <div className="text-sm font-semibold text-foreground">
                  {usersModalAcl?.acl_name || '—'}
                </div>
              </div>
              <div className="ml-auto">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total Assigned
                </span>
                <div>
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary ring-1 ring-inset ring-primary/25 tabular-nums">
                    {usersModalAcl?.assigned_user_count ??
                      (usersModalAcl?.assigned_users?.length || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="w-12 px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    #
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Username
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {(usersModalAcl?.assigned_users?.length || 0) === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-sm text-muted-foreground">
                      No users assigned.
                    </td>
                  </tr>
                ) : (
                  usersModalAcl.assigned_users.map((u: any, idx: number) => (
                    <tr
                      key={u.id ?? idx}
                      className={
                        'border-t border-border/40 transition-colors hover:bg-muted/30 ' +
                        (idx % 2 === 1 ? 'bg-muted/15' : '')
                      }
                    >
                      <td className="px-4 py-2.5 text-xs tabular-nums text-muted-foreground">
                        {idx + 1}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-foreground">
                        {u.username || '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
                          {u.role || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUsersModalAcl(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default ACLSettingList;
